import React, { useState, useEffect } from 'react';
import { analysisAPI } from '../../services/api';
import { Search, Filter, Download, Eye, CheckCircle, XCircle, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

const Analyses = () => {
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [perPage, setPerPage] = useState(15);
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  const getRecordUrl = (recordPath) => {
    if (!recordPath) return null;
    if (typeof recordPath !== 'string') return null;
    if (/^https?:\/\//i.test(recordPath)) return recordPath;
    
    // Utiliser l'URL de base dynamique depuis les variables d'environnement
    const getApiBaseUrl = () => {
      const env = process.env.REACT_APP_ENV || 'development';
      
      if (env === 'production') {
        return process.env.REACT_APP_API_URL_PRODUCTION || 'https://manageo-api.hoststronger.site';
      }
      
      return process.env.REACT_APP_API_URL_DEVELOPMENT || 'http://localhost:8000';
    };
    
    const baseUrl = getApiBaseUrl();
    const sanitized = recordPath.replace(/^\//, '');
    return `${baseUrl}/${sanitized}`;
  };


  useEffect(() => {
    fetchAnalyses();
  }, [currentPage, perPage, searchTerm, dateStart, dateEnd, sortBy, sortOrder]);

  const fetchAnalyses = async () => {
    try {
      setLoading(true);
      const response = await analysisAPI.getCalls({
        page: currentPage,
        per_page: perPage,
        search: searchTerm,
        date_start: dateStart,
        date_end: dateEnd,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      
      setAnalyses(response.data.data || []);
      setTotalPages(response.data.last_page || 1);
    } catch (err) {
      setError('Erreur lors du chargement des analyses');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchAnalyses();
  };

  const handlePerPageChange = (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      // Toggle sort order if clicking on the same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to descending
      setSortBy(field);
      setSortOrder('desc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    }
    return sortOrder === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-blue-600" />
      : <ArrowDown className="h-4 w-4 text-blue-600" />;
  };

  const getVisiblePages = () => {
    const maxVisible = 5;
    const half = Math.floor(maxVisible / 2);
    let start = Math.max(1, currentPage - half);
    let end = Math.min(totalPages, start + maxVisible - 1);
    
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }
    
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('fr-FR');
  };

  if (loading && analyses.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Analyses des appels</h1>
        <button
          onClick={fetchAnalyses}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition duration-200"
        >
          Actualiser
        </button>
      </div>

      {/* Barre de recherche et filtres */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Rechercher par ID d'appel, email utilisateur..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition duration-200"
            >
              Rechercher
            </button>
          </div>
          
          {/* Filtres de date */}
          <div className="flex gap-4 items-center">
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-gray-400" />
              <span className="text-sm font-medium text-gray-700">Filtres :</span>
            </div>
            <div className="flex gap-4">
              <div className="flex flex-col">
                <label className="text-xs text-gray-600 mb-1">Date début</label>
                <input
                  type="date"
                  value={dateStart}
                  onChange={(e) => setDateStart(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-xs text-gray-600 mb-1">Date fin</label>
                <input
                  type="date"
                  value={dateEnd}
                  onChange={(e) => setDateEnd(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={() => {
                    setDateStart('');
                    setDateEnd('');
                    setCurrentPage(1);
                  }}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition duration-200"
                >
                  Réinitialiser
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Tableau des analyses */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th 
                  onClick={() => handleSort('call_id')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-1">
                    <span>ID Appel</span>
                    {getSortIcon('call_id')}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('user_firstname')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-1">
                    <span>Utilisateur</span>
                    {getSortIcon('user_firstname')}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('incall_duration')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-1">
                    <span>Durée</span>
                    {getSortIcon('incall_duration')}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('call_time')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-1">
                    <span>Heure Appel</span>
                    {getSortIcon('call_time')}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('created_at')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-1">
                    <span>Date</span>
                    {getSortIcon('created_at')}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('call_type')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-1">
                    <span>Type d'Appel</span>
                    {getSortIcon('call_type')}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('hierarchy_effort_percentage')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-1">
                    <span>Effort Hiérarchie</span>
                    {getSortIcon('hierarchy_effort_percentage')}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Transcription
                </th>
                <th 
                  onClick={() => handleSort('has_lead')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-1">
                    <span>Lead</span>
                    {getSortIcon('has_lead')}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {analyses.map((analysis) => (
                <tr key={analysis.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {analysis.call_id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div>
                      <div className="font-medium">{analysis.user_firstname || 'N/A'}</div>
                      <div className="text-xs text-gray-400">{analysis.user_email || 'N/A'}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div>
                      <div>Incall: {formatDuration(analysis.incall_duration)}</div>
                      <div className="text-xs text-gray-400">
                        Total: {formatDuration(analysis.total_duration)}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(analysis.call_time)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(analysis.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {analysis.call_type_label ? (
                      <div>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          analysis.call_type === 1 ? 'bg-purple-100 text-purple-800' :
                          analysis.call_type === 2 ? 'bg-yellow-100 text-yellow-800' :
                          analysis.call_type === 3 ? 'bg-gray-100 text-gray-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          Type {analysis.call_type}
                        </span>
                        <div className="text-xs text-gray-500 mt-1">
                          {analysis.call_type_label}
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">N/A</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {analysis.hierarchy_effort_percentage !== null && analysis.hierarchy_effort_percentage !== undefined ? (
                      <div className="flex items-center">
                        <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                          <div 
                            className={`h-2 rounded-full ${
                              analysis.hierarchy_effort_percentage >= 70 ? 'bg-green-500' :
                              analysis.hierarchy_effort_percentage >= 40 ? 'bg-yellow-500' :
                              'bg-red-500'
                            }`}
                            style={{ width: `${Math.min(100, analysis.hierarchy_effort_percentage)}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-semibold text-gray-700">
                          {Math.round(analysis.hierarchy_effort_percentage)}%
                        </span>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">N/A</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      analysis.transcription 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {analysis.transcription ? 'Disponible' : 'Indisponible'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      analysis.has_lead 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {analysis.has_lead ? '✓ Lead' : '✗ Pas de lead'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => setSelectedAnalysis(analysis)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Contrôles de pagination */}
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">Éléments par page:</label>
              <select
                value={perPage}
                onChange={(e) => handlePerPageChange(Number(e.target.value))}
                className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value={15}>15</option>
                <option value={30}>30</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <p className="text-sm text-gray-700">
              Page <span className="font-medium">{currentPage}</span> sur{' '}
              <span className="font-medium">{totalPages}</span>
            </p>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Précédent
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Suivant
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-center">
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Précédent
                </button>
                {getVisiblePages().map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      currentPage === page
                        ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Suivant
                </button>
              </nav>
            </div>
          </div>
        )}
      </div>

      {/* Modal de détails */}
      {selectedAnalysis && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                Détails de l'analyse - {selectedAnalysis.call_id}
              </h3>
              <button
                onClick={() => setSelectedAnalysis(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Utilisateur</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedAnalysis.user_firstname} ({selectedAnalysis.user_email})
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">IVR</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedAnalysis.ivr_name || 'N/A'}
                  </p>
                </div>
              </div>

              {selectedAnalysis.record_path && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Enregistrement
                  </label>
                  <audio
                    controls
                    preload="none"
                    className="w-full"
                    src={getRecordUrl(selectedAnalysis.record_path)}
                  />
                </div>
              )}
              
              {/* Classification de l'appel */}
              {selectedAnalysis.call_classification && (
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-lg border border-purple-200">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <span className="bg-purple-600 text-white rounded-full w-8 h-8 flex items-center justify-center mr-2 text-sm">🎯</span>
                    Classification Automatique de l'Appel
                  </h4>
                  
                  <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <span className={`inline-flex px-3 py-2 text-sm font-bold rounded-full ${
                          selectedAnalysis.call_type === 1 ? 'bg-purple-100 text-purple-800' :
                          selectedAnalysis.call_type === 2 ? 'bg-yellow-100 text-yellow-800' :
                          selectedAnalysis.call_type === 3 ? 'bg-gray-100 text-gray-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          TYPE {selectedAnalysis.call_type} - {selectedAnalysis.call_type_label}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">
                        Confiance: <span className="font-semibold">{selectedAnalysis.call_classification.confidence}%</span>
                      </div>
                    </div>
                    <div className="text-sm text-gray-700 mb-2">
                      <strong>Justification:</strong> {selectedAnalysis.call_classification.justification}
                    </div>
                    <div className="text-sm text-gray-600">
                      <strong>Indicateurs:</strong> {selectedAnalysis.call_classification.indicators}
                    </div>
                  </div>
                  
                  {/* Règles respectées selon le type */}
                  {selectedAnalysis.rules_compliance && (
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                      <h5 className="text-sm font-semibold text-gray-900 mb-3">Respect des Règles</h5>
                      
                      {selectedAnalysis.call_type === 1 && selectedAnalysis.rules_compliance.type === 'ivr' && (
                        <div className="space-y-2">
                          <div className="flex items-center">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              selectedAnalysis.rules_compliance.all_steps_followed 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {selectedAnalysis.rules_compliance.all_steps_followed ? '✓ Toutes les étapes suivies' : '✗ Étapes manquantes'}
                            </span>
                          </div>
                          <div className="text-sm text-gray-700">
                            {selectedAnalysis.rules_compliance.evaluation}
                          </div>
                          {selectedAnalysis.rules_compliance.missing_steps && selectedAnalysis.rules_compliance.missing_steps.length > 0 && (
                            <div className="text-xs text-red-600 mt-2">
                              Étapes manquantes: {selectedAnalysis.rules_compliance.missing_steps.join(', ')}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {selectedAnalysis.call_type === 2 && selectedAnalysis.rules_compliance.type === 'waiting' && (
                        <div className="space-y-2">
                          <div className="flex items-center">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              selectedAnalysis.rules_compliance.waited_60_seconds 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {selectedAnalysis.rules_compliance.waited_60_seconds ? '✓ Attente de 60s respectée' : '✗ Attente insuffisante'}
                            </span>
                          </div>
                          <div className="text-sm text-gray-700">
                            {selectedAnalysis.rules_compliance.evaluation}
                          </div>
                          <div className="text-xs text-gray-600">
                            Durée réelle: {selectedAnalysis.rules_compliance.actual_duration} secondes
                          </div>
                        </div>
                      )}
                      
                      {selectedAnalysis.call_type === 3 && (
                        <div className="text-sm text-gray-700">
                          {selectedAnalysis.rules_compliance.message}
                        </div>
                      )}
                      
                      {selectedAnalysis.call_type === 4 && (
                        <div className="text-sm text-gray-700">
                          {selectedAnalysis.rules_compliance.message}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {selectedAnalysis.effort_analysis && (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <span className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center mr-2 text-sm">IA</span>
                    Analyse de l'Effort pour Atteindre un Responsable Hiérarchique
                  </h4>
                  
                  {/* Pourcentage d'effort */}
                  {selectedAnalysis.hierarchy_effort_percentage !== null && selectedAnalysis.hierarchy_effort_percentage !== undefined && (
                    <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
                      <h5 className="text-sm font-semibold text-gray-900 mb-3">Pourcentage d'Effort Global</h5>
                      <div className="flex items-center">
                        <div className="flex-1 bg-gray-200 rounded-full h-4 mr-4">
                          <div 
                            className={`h-4 rounded-full transition-all duration-500 ${
                              selectedAnalysis.hierarchy_effort_percentage >= 70 ? 'bg-green-500' :
                              selectedAnalysis.hierarchy_effort_percentage >= 40 ? 'bg-yellow-500' :
                              'bg-red-500'
                            }`}
                            style={{ width: `${Math.min(100, selectedAnalysis.hierarchy_effort_percentage)}%` }}
                          ></div>
                        </div>
                        <span className="text-2xl font-bold text-gray-900">
                          {Math.round(selectedAnalysis.hierarchy_effort_percentage)}%
                        </span>
                      </div>
                      <div className="mt-2 flex gap-2">
                        {selectedAnalysis.effort_analysis.a_demande_responsable && (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            ✓ A demandé un responsable
                          </span>
                        )}
                        {selectedAnalysis.effort_analysis.a_insiste && (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            ✓ A insisté
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-4">
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                        </div>
                        <div className="ml-3 flex-1">
                          <h5 className="text-sm font-semibold text-gray-900 mb-2">Évaluation Détaillée</h5>
                          <p className="text-sm text-gray-700 leading-relaxed">
                            {selectedAnalysis.effort_analysis.evaluation}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {selectedAnalysis.effort_analysis.points_forts && (
                      <div className="bg-white p-4 rounded-lg shadow-sm">
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                          </div>
                          <div className="ml-3 flex-1">
                            <h5 className="text-sm font-semibold text-gray-900 mb-2">Points Forts</h5>
                            <p className="text-sm text-gray-700 leading-relaxed">
                              {selectedAnalysis.effort_analysis.points_forts}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {selectedAnalysis.effort_analysis.points_amelioration && (
                      <div className="bg-white p-4 rounded-lg shadow-sm">
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2"></div>
                          </div>
                          <div className="ml-3 flex-1">
                            <h5 className="text-sm font-semibold text-gray-900 mb-2">Points d'Amélioration</h5>
                            <p className="text-sm text-gray-700 leading-relaxed">
                              {selectedAnalysis.effort_analysis.points_amelioration}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                        </div>
                        <div className="ml-3 flex-1">
                          <h5 className="text-sm font-semibold text-gray-900 mb-2">Résumé</h5>
                          <p className="text-sm text-gray-700 leading-relaxed italic">
                            {selectedAnalysis.effort_analysis.explication}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {selectedAnalysis.transcription && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Transcription
                  </label>
                  <div className="bg-gray-50 p-4 rounded-lg max-h-96 overflow-y-auto">
                    <pre className="whitespace-pre-wrap text-sm text-gray-900">
                      {selectedAnalysis.transcription}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Analyses;
